import React, { type FC } from 'react'
import { useSelector } from 'react-redux'
import { fundsManagementTracker } from '@mobi/account/Common/Analytics'
import { useHostContextFeature } from '@mobi/account/Hooks'
import { track } from '@mobi/account/Utils/analytics'
import { type PayIdDetails } from '@mobi/api-types/src/account/betAccount'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import { CustomerServiceCentre } from '@mobi/component-library/Base/Link'
import { colors } from '@mobi/component-library/Foundations'
import { spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { CopyField } from '@mobi/component-library/Selection&Input/CopyField'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { usePayID } from '../../../Hooks/PayID/usePayID'
import { selectDepositFlow } from '../../../Store'
import type { PanelProps } from '../../types'
import { DepositLimitRow } from '../DepositLimitRow'
import FastWithdrawalInstructionCard from './FastWithdrawalInstructionCard'
import { PayIDGenerateButton } from './PayIDGenerateButton'
import PayIDInstructions from './PayIDInstructions'
import { InfoListStyled, MissingPayIDCardStyled, PayIDPanelStyled } from './PayIDPanel.styled'

type Props = Pick<PanelProps, 'accountNumber'> & {
  payIdDetailList: PayIdDetails[]
  depositLimits: { minimum: number; maximum: number }
  remainingDepositAmount: number
  remainingDepositTransactions: number
}

export const PayIDPanel: FC<Props> = ({
  accountNumber,
  payIdDetailList,
  depositLimits,
  remainingDepositAmount,
  remainingDepositTransactions,
}) => {
  const fastWithdrawalMarketingBanner = useHostContextFeature(
    FeatureFlags.FAST_WITHDRAW_MARKETING_BANNER.key
  )
  const isWithdrawRealtimeEnabled = useHostContextFeature(FeatureFlags.WITHDRAW_REALTIME.key)
  const isDepositValueVolumeLimitsEnabled = useHostContextFeature(
    FeatureFlags.DEPOSIT_VALUE_VOLUME_LIMITS.key
  )

  const flow = useSelector(selectDepositFlow)
  const { activePayID, generate, status } = usePayID({
    accountNumber,
    payIdDetailList,
    location: 'Deposit',
  })

  const heading = (() => {
    if (status === 'loading') {
      return "We're still working on generating your PayID. Feel free to leave this page and check back again in a few minutes to see if it's ready."
    }

    if (status === 'retry') {
      return "We're unable to generate your PayID. Please try again."
    }

    if (status === 'error') {
      return (
        <CustomerServiceCentre
          prefixText='We’re unable to generate your PayID right now.'
          key='pay-id-error'
          onClickPhoneNumber={() =>
            fundsManagementTracker({ interaction_type: 'Need Assistance', location: 'Deposit' })
          }
        />
      )
    }

    return 'Generate your unique TABtouch PayID.'
  })()

  const onPayIDCopied = () =>
    track('deposit_copy_content', {
      accountNumber,
      paymentMethod: 'PayID',
      copiedContent: 'PayID',
      copiedLocation: 'PayID',
      depositVariant: flow,
    })

  return (
    <PayIDPanelStyled>
      <header>
        <p>
          Instantly transfer funds into your TABtouch account, direct from your banking app. It's
          fast and secure.
        </p>
        <br />
        <p>The name on your bank account must match the name on your TABtouch account.</p>
      </header>

      {activePayID && (
        <InfoListStyled>
          <dt>PayID</dt>
          <dd>
            <CopyField value={activePayID.PayId} onCopied={onPayIDCopied} />
          </dd>

          <dt>Minimum Deposit</dt>
          <dd>$5.00</dd>

          {isDepositValueVolumeLimitsEnabled && (
            <DepositLimitRow
              depositLimits={depositLimits}
              remainingDepositAmount={remainingDepositAmount}
              remainingDepositTransactions={remainingDepositTransactions}
              showDepositLimitLink={flow !== 'quick-deposit'}
            />
          )}
        </InfoListStyled>
      )}

      {!activePayID && (
        <MissingPayIDCardStyled>
          <Flex alignItems='center' justifyContent='center'>
            <SvgLazy name='PayIDLogo' color={colors.white} width='5rem' />
          </Flex>

          <div>
            <p>{heading}</p>

            <PayIDGenerateButton status={status} onGenerate={generate} />
          </div>
        </MissingPayIDCardStyled>
      )}

      <PayIDInstructions location='Deposit' />

      {isWithdrawRealtimeEnabled && fastWithdrawalMarketingBanner && (
        <div style={{ marginTop: spacing.md }}>
          <FastWithdrawalInstructionCard />
        </div>
      )}
    </PayIDPanelStyled>
  )
}

PayIDPanel.displayName = 'PayIDPanel'
